import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { SelectionModel } from '@angular/cdk/collections';
import { PageEvent } from '@angular/material/paginator';
import {
  BehaviorSubject,
  Observable,
  of,
  Subscription
} from 'rxjs';
import { map, tap, debounceTime } from 'rxjs/operators';
import { Router } from '@angular/router';
import { indicate } from 'src/app/operators';
import { PipingAndValvesNavigationService } from 'src/app/piping-valves/services/piping-and-valves-navigation.service';
import { FilterablePropertyFactory } from 'src/app/shared/components/factory/filterable-property-factory.service';
import {
  FilterableProperty,
  SearchRequest,
} from 'src/app/shared/models/autogenerated-coating';
import {
  EsraPipingAPIClient,
  FilterType,
  PipingFilterCategory,
  PipingObjectType,
  PipingSearchFilterModel,
  PipingSearchResultViewModel,
} from 'src/app/shared/models/autogenerated-piping';
import { TechnologyEnum } from 'src/app/shared/models/technology-enum';
import { CelsiusToFahrenheitPipe } from 'src/app/shared/pipes/celcius-to-fahrenheit.pipe';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { environment } from 'src/environments/environment';
import { MatPaginatorGoToComponent } from '../../../shared/components/mat-paginator-go-to/mat-paginator-go-to.component';
import { FilterDataService } from '../../services/filter.service';
import { RangeFilterHelper } from '../common/range-filter.helper';
import { MatDialog } from '@angular/material/dialog';
import { ValveSpecModalComponent, ValveSpecModalData } from '../../valve-spec-modal/valve-spec-modal.component';
import { UserService } from 'src/app/core/services/user.service';
import { ProductPanelHelper } from 'src/app/shared/components/product-panel/product-panel-helper';

@Component({
    selector: 'app-piping-admin-search',
    templateUrl: './piping-admin-search.component.html',
    styleUrls: ['./piping-admin-search.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class PipingAdminSearchComponent implements OnInit, OnDestroy {
  constructor(
    private readonly esraPipingAPIClient: EsraPipingAPIClient,
    private readonly filterDataService: FilterDataService,
    public rangeFilterHelper: RangeFilterHelper,
    public navigator: PipingAndValvesNavigationService,
    private readonly snackBarService: SnackBarService,
    private readonly cd: ChangeDetectorRef,
    private readonly dialog: MatDialog,
    private userService: UserService,
    private readonly router: Router
  ) { }

  createNewValve(): void {
    this.router.navigate(['/piping/valve/edit'], {
      queryParams: { mode: 'create' }
    });
  }

  // Modal mode properties
  @Input() isModalMode: boolean = false;
  @Input() searchTypeFilter: 'pipes' | 'valves' | 'both' = 'both';
  @Input() existingValveIds: string[] = [];
  @Output() valvesSelected = new EventEmitter<any[]>();

  selectedItems: any[] = [];

  // Control variables
  isModalInitializing = false;
  private originalTypeSelectedSubject: BehaviorSubject<PipingObjectType>;
  private modalTypeOverride: PipingObjectType | null = null;

  // Table configuration for modal mode
  modalTableColumns: string[] = ['select', 'tag', 'type', 'size', 'rating', 'material', 'description'];
  modalTableDisplayedColumns: string[] = ['select', 'tag', 'type', 'sch1', 'end', 'description'];
  selection = new SelectionModel<any>(true, []);

  searchText = '';
  technology: TechnologyEnum = TechnologyEnum.PIPES;

  CSHARP_INT_MAX = 2147483647;

  lastPage: number;
  totalCount: number;
  currentPage = 0;
  pageSize = 10;
  pageSizes = [2, 5, 10, 25, 50, 100];

  // object types
  _both = PipingObjectType._0;
  _pipe = PipingObjectType._1;
  _valve = PipingObjectType._2;

  // filter categories
  _commonFilter = PipingFilterCategory._3;
  _pipeFilter = PipingFilterCategory._1;
  _valveFilter = PipingFilterCategory._2;

  // input ids
  _pipesCheckBoxId = 'pipes-check-button';
  _valvesCheckBoxId = 'valves-check-button';

  // temperature units
  _fahrenheit = 'fahrenheit';
  _celsius = 'celcius';

  // comparison units
  _equals = 'equals (=)';
  _greaterThan = 'greater than (>)';
  _greaterThanEquals = 'greater or equals (≥)';
  _lessThan = 'less than (<)';
  _lessThanEquals = 'less than or equals (≤)';

  // default step values for nominal corrosion allowance filter

  _nominalCorrosionAllowanceDefaultStepValue = 0.000;
  _nominalCorrosionAllowanceStep = 0.0625;

  _defaultNominalCorrosionAllowanceComparisonSelectionString = this._equals;
  _defaultMaximumTemperatureComparisonSelectionString = this._greaterThanEquals;
  _defaultMinimumTemperatureComparisonSelectionString = this._lessThanEquals;
  nominalCorrosionAllowanceComparisonOptions: Array<string> = [
    this._equals,
    this._greaterThanEquals,
    this._greaterThan,
    this._lessThan,
    this._lessThanEquals,
  ];

  maximumTemperatureComparisonOptions: Array<string> = [
    this._greaterThanEquals,
    this._greaterThan,
    this._equals,
  ];

  minimumTemperatureComparisonOptions: Array<string> = [
    this._lessThanEquals,
    this._lessThan,
    this._equals,
  ];

  selectedNominalCorrosionAllowanceComparisonOption =
    this._defaultNominalCorrosionAllowanceComparisonSelectionString;
  selectedMaximumTemperatureComparisonOption =
    this._defaultMaximumTemperatureComparisonSelectionString;
  selectedMinimumTemperatureComparisonOption =
    this._defaultMinimumTemperatureComparisonSelectionString;

  selectedTemperatureUnit = this._fahrenheit;

  /** Current value in the minimum temperature input within the combined temperature accordion. */
  minTempInputText: string = '';
  /** Current value in the maximum temperature input within the combined temperature accordion. */
  maxTempInputText: string = '';
  /** Current value in the nominal corrosion allowance filter input. Cleared on filter reset. */
  ncaInputText: string = '';

  typeSelectedSubject = new BehaviorSubject<PipingObjectType>(this._both);
  typeSelected$ = this.typeSelectedSubject.asObservable();
  typeSelected: PipingObjectType = this._both; // Will be overridden in modal mode IMMEDIATELY

  newFilters: PipingSearchFilterModel[] = [];
  originalFilters: PipingSearchFilterModel[] = [];
  isOriginalFiltersSet = false;
  latestFilterSelection = '';

  pipeResults$: Observable<PipingSearchResultViewModel[]>;
  filterableProperties: FilterableProperty[];
  filterablePropertiesFlattened: FilterableProperty[];

  pipeFilterCount: number;
  valveFilterCount: number;
  commonFilterCount: number;

  inputNumStr: string;
  pipe = new CelsiusToFahrenheitPipe();
  searchResultObjects: PipingSearchResultViewModel[];

  inProduction: boolean;
  filterDisplayArchivedItems = false;
  isFilterBeingRemoved: boolean;

  /** When true, the search includes archived, draft, and retired items. Effective for admin users only. */
  includeNonCurrent: boolean = false;

  valveId: string;
  valveSelected: boolean = false;
  pipeClassId: string;
  pipeClassSelected: boolean = false;

  //indicator subjects
  searching$ = new BehaviorSubject<boolean>(false);
  loadingFilters$ = new BehaviorSubject<boolean>(false);
  exportingPdf$ = new BehaviorSubject<boolean>(false);

  /**
   * Stable reference for the synthetic "non-current versions" chip.
   * Must not be recreated on each CD cycle — *ngFor uses object identity to track
   * chips, so a new object every cycle destroys and recreates the Material chip
   * element before the (removed) click event can complete.
   */
  private readonly _nonCurrentChip: FilterableProperty = FilterablePropertyFactory.createFilterbleProperty(
    FilterType._6,
    'IncludeNonCurrent',
    'non-current versions',
    ['non-current versions']
  );

  /**
   * Full unfiltered options for each filter panel, captured on the first (unfiltered) search.
   * Used to restore filter panels after any interaction — preserving the principle that
   * a filter with no active selection shows every available option.
   */
  private readonly _baseFilterOptions: Map<string, string[]> = new Map<string, string[]>();

  private readonly subscription = new Subscription();

  @ViewChild('paginator') paginator: MatPaginatorGoToComponent;
  @ViewChild('pipesCheckButton')
  pipesRadioButtonInput: ElementRef<HTMLInputElement>;
  @ViewChild('valvesCheckButton')
  valvesRadioButtonInput: ElementRef<HTMLInputElement>;
  @ViewChild('fahrenheitCheckButton')
  fahrenheitRadioButtonInput: ElementRef<HTMLInputElement>;
  @ViewChild('celsiusCheckButton')
  celsiusRadioButtonInput: ElementRef<HTMLInputElement>;

  isPipingAndValvesAdmin$: Observable<boolean>;

  /** The MINIMUM filterType entry from originalFilters; drives the combined temperature accordion. */
  get minimumTemperatureFilter(): PipingSearchFilterModel | undefined {
    return this.originalFilters.find(f => f.filterType === 'MINIMUM');
  }

  /** The MAXIMUM filterType entry from originalFilters; drives the combined temperature accordion. */
  get maximumTemperatureFilter(): PipingSearchFilterModel | undefined {
    return this.originalFilters.find(f => f.filterType === 'MAXIMUM');
  }

  ngOnInit() {
    this.isPipingAndValvesAdmin$ = this.userService.isPipingAndValvesAdminObservable();
    // IMMEDIATELY set the correct type for modal mode BEFORE anything else
    if (this.isModalMode) {
      if (this.searchTypeFilter === 'valves') {
        this.typeSelected = this._valve;
        this.typeSelectedSubject = new BehaviorSubject<PipingObjectType>(this._valve);
        this.typeSelected$ = this.typeSelectedSubject.asObservable();
        this.modalTypeOverride = this._valve;
        this.includeNonCurrent = true;
      } else if (this.searchTypeFilter === 'pipes') {
        this.typeSelected = this._pipe;
        this.typeSelectedSubject = new BehaviorSubject<PipingObjectType>(this._pipe);
        this.typeSelected$ = this.typeSelectedSubject.asObservable();
        this.modalTypeOverride = this._pipe;
        this.includeNonCurrent = false;
      }
      this.originalTypeSelectedSubject = this.typeSelectedSubject;
      this.setupModalTypeProtection();
    }

    this.subscription.add(
      this.filterDataService.currentFiltersSelection$
        .pipe(
          debounceTime(this.isModalMode ? 50 : 0), // Small debounce for modal mode
          map((filterSelection: Map<string, FilterableProperty>) => {

            // Force correct ObjectType in modal mode
            if (this.isModalMode && this.searchTypeFilter === 'valves') {
              const objectTypeFilter = filterSelection.get('ObjectType');
              if (objectTypeFilter) {
                objectTypeFilter.filterOptions = ['VALVE'];
              }
            } else if (this.isModalMode && this.searchTypeFilter === 'pipes') {
              const objectTypeFilter = filterSelection.get('ObjectType');
              if (objectTypeFilter) {
                objectTypeFilter.filterOptions = ['PIPE'];
              }
            }
            return filterSelection;
          }),
          map((filterSelection: Map<string, FilterableProperty>) => {
            this.filterableProperties = Array.from(filterSelection.values());
            this.filterablePropertiesFlattened = this.filterableProperties;
            this.currentPage = 0;
            this.search();
          })
        )
        .subscribe()
    );
    this.inProduction = environment.production;

    // Reset combined temperature accordion inputs when filters are cleared.
    this.subscription.add(
      this.filterDataService.getResetSubject().pipe(
        tap((resetRequest: boolean) => {
          if (resetRequest) {
            this.minTempInputText = '';
            this.maxTempInputText = '';
            this.ncaInputText = '';
            this.selectedMinimumTemperatureComparisonOption = this._defaultMinimumTemperatureComparisonSelectionString;
            this.selectedMaximumTemperatureComparisonOption = this._defaultMaximumTemperatureComparisonSelectionString;
            this.selectedNominalCorrosionAllowanceComparisonOption = this._defaultNominalCorrosionAllowanceComparisonSelectionString;
          }
        })
      ).subscribe()
    );

    // Configure for modal mode
    if (this.isModalMode) {
      this.isModalInitializing = true;
      this.cd.detectChanges();

      // Clear existing filters first
      this.filterDataService.removeAllFilters();

      // Apply the object type filter and search ONCE
      this.setObjectTypeFilter();
      // Don't call search() here - it will be called by the subscription

      // Only the UI update needs timeout for change detection
      setTimeout(() => {
        this.isModalInitializing = false;
        this.cd.detectChanges();
      }, 100);
    }

    this.cd.detectChanges();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    if (!this.isModalMode) {
      this.resetFilters();
    }
  }

  callSearch(e: string): void {
    this.searchText = e;
    this.search();
  }

  private search(): void {

    // Force correct type in modal mode before search
    if (this.isModalMode) {
      if (this.searchTypeFilter === 'valves') {
        this.typeSelected = this._valve;
        this.typeSelectedSubject.next(this._valve);
      } else if (this.searchTypeFilter === 'pipes') {
        this.typeSelected = this._pipe;
        this.typeSelectedSubject.next(this._pipe);
      }
    }

    const request: SearchRequest = new SearchRequest({
      page: this.currentPage,
      perPage: this.pageSize,
      mainSearch: this.wrapSearchTextInWildcardsConditionally(
        this.searchText
      ),
      filters: this.filterableProperties
    });

    this.technology = Number(TechnologyEnum.PIPES);
    this.subscription.add(
      this.esraPipingAPIClient
        .searchPipingAndValves(this.includeNonCurrent, request)
        .pipe(
          indicate(this.searching$),
          tap((response) => {
            this.setFilters(response);
            this.pipeResults$ = of(response.results);
            this.searchResultObjects = response.results;
            this.totalCount = response.totalResults;
            this.lastPage = Math.ceil(this.totalCount / this.pageSize);
          })
        )
        .subscribe()
    );
  }

  setFilters(response): void {
    // Treat a search with no active filters as an unfiltered result and use it to
    // rebuild the base option set. This also handles the edge case where the singleton
    // FilterDataService carried leftover filters from a previous page, causing the very
    // first search to be filtered; once the user clears those, the base is corrected.
    const searchWasUnfiltered: boolean = !this.filterableProperties?.length;

    if (searchWasUnfiltered || !this.isOriginalFiltersSet) {
      this.originalFilters = this.sortFilters(response.pipingSearchFilters);
      this.originalFilters.forEach(filter => {
        this._baseFilterOptions.set(filter.filterPropertyName ?? '', [...(filter.options ?? [])]);
      });
      this.isOriginalFiltersSet = true;
    } else {
      // Always restore every filter panel to the full base options — no cascading.
      // A filter with no active selection shows every available option. Checked state is
      // kept in sync by the multi-select-filter component's own selectUnselect logic.
      this.originalFilters.forEach((originalFilter) => {
        const baseOptions: string[] | undefined = this._baseFilterOptions.get(originalFilter.filterPropertyName ?? '');
        if (baseOptions !== undefined) {
          originalFilter.options = [...baseOptions];
        }
      });
    }
    this.setFilterCounts(this.originalFilters);
    this.sortFilters(this.originalFilters);

  }

  public sortFilters(filters: PipingSearchFilterModel[]): PipingSearchFilterModel[] {
    const newFilter = filters.filter(
      (filter) => filter.options && filter.options.length > 0
    );
    newFilter.forEach((filter) => {
      filter.options.sort((a, b) => a.localeCompare(b, undefined, {
        numeric: true,
        sensitivity: 'base',
      })
      );
    });
    newFilter.sort((a, b) => (a.displayOrder - b.displayOrder));

    return newFilter;
  }

  // Modal mode selection methods
  toggleItemSelection(item: any): void {
    if (!this.isModalMode) return;

    const index = this.selectedItems.findIndex(selected => selected.id === item.id);
    if (index > -1) {
      this.selectedItems.splice(index, 1);
      this.selection.deselect(item);
    } else {
      this.selectedItems.push(item);
      this.selection.select(item);
    }

    this.valvesSelected.emit(this.selectedItems);
  }

  isItemSelected(item: any): boolean {
    return this.selectedItems.some(selected => selected.id === item.id);
  }

  isItemDisabled(item: any): boolean {
    return this.existingValveIds.includes(item.id);
  }

  configureModalCheckboxes(): void {
    // Method kept for compatibility - configuration now handled in ngOnInit
    return;
  }

  // Table selection methods for modal mode
  isAllSelected(): boolean {
    if (!this.searchResultObjects || this.searchResultObjects.length === 0) {
      return false;
    }
    const enabledItems = this.searchResultObjects.filter(item => !this.isItemDisabled(this.getEntity(item)));
    return enabledItems.length === this.selectedItems.length;
  }

  masterToggle(): void {
    if (!this.searchResultObjects) return;

    if (this.isAllSelected()) {
      // Deselect all
      this.selectedItems = [];
      this.selection.clear();
    } else {
      // Select all enabled items
      const enabledItems = this.searchResultObjects
        .filter(item => !this.isItemDisabled(this.getEntity(item)))
        .map(item => this.getEntity(item));

      this.selectedItems = [...enabledItems];
      this.selection.clear();
      enabledItems.forEach(item => this.selection.select(item));
    }

    this.valvesSelected.emit(this.selectedItems);
  }

  public addSearchResultsToWorkspace() {
    const request: SearchRequest = new SearchRequest();
    request.perPage = this.CSHARP_INT_MAX;
    request.page = 0;
    request.mainSearch = this.searchText;
    request.filters = this.filterableProperties;

    this.subscription.add(
      this.esraPipingAPIClient
        .addSearchRequestPipeAndValveClassesToWorkspace(request)
        .pipe(
          indicate(this.searching$),
          tap({
            next: () => {
              this.snackBarService.showSnackBar(
                true,
                'All search results were added to your workspace'
              );
            },
            error: (err) => {
              this.snackBarService.showError(err.message);
            },
          })
        )
        .subscribe()
    );
  }

  /** Triggered when a pipe class is successfully retired from its result card. Refreshes the search results. */
  onPipeClassRetired(): void {
    this.search();
  }

  onArchiveValve(valveId: string) {
    if (!valveId) {
      this.snackBarService.showSnackBar(true, 'Unable to archive: valve id is missing');
      return;
    }

    this.esraPipingAPIClient.archiveValve(valveId)
      .pipe(
        indicate(this.searching$),
      )
      .subscribe({
        next: () => {
          this.snackBarService.showSnackBar(true, 'Valve archived successfully');
        },
        error: (err) => {
          const msg =
            err?.response ??
            err?.error?.message ??
            err?.message ??
            'Failed to archive valve';
          this.snackBarService.showSnackBar(true, msg);
        },

      });
  }

  private createFilterableProperty(
    filterType: FilterType,
    propertyName: string,
    filterDisplayName: string,
    filterOptions: string[]
  ): FilterableProperty {
    const filterableProperty = new FilterableProperty();
    filterableProperty.filterType = filterType;
    filterableProperty.propertyName = propertyName;
    filterableProperty.displayName = filterDisplayName;
    filterableProperty.filterOptions = filterOptions;
    return filterableProperty;
  }

  private setupModalTypeProtection() {

    if (this.searchTypeFilter === 'valves') {
      this.modalTypeOverride = this._valve;
    } else if (this.searchTypeFilter === 'pipes') {
      this.modalTypeOverride = this._pipe;
    } else {
      this.modalTypeOverride = this._both;
    }

    // Override the next method to prevent changes in modal mode
    const originalNext = this.typeSelectedSubject.next.bind(this.typeSelectedSubject);
    this.typeSelectedSubject.next = (value: PipingObjectType) => {

      if (this.isModalMode && this.modalTypeOverride !== null) {
        // Force the modal type instead of the requested value
        originalNext(this.modalTypeOverride);
        this.typeSelected = this.modalTypeOverride;
      } else {
        originalNext(value);
        this.typeSelected = value;
      }
    };
  }

  private setTypeSelection(newType: PipingObjectType) {

    // In modal mode, never change from the configured type
    if (this.isModalMode) {
      if (this.searchTypeFilter === 'valves') {
        this.typeSelectedSubject.next(this._valve);
        this.typeSelected = this._valve;
      } else if (this.searchTypeFilter === 'pipes') {
        this.typeSelectedSubject.next(this._pipe);
        this.typeSelected = this._pipe;
      } else {
        this.typeSelectedSubject.next(this._both);
        this.typeSelected = this._both;
      }
    } else {
      this.typeSelectedSubject.next(newType);
      this.typeSelected = newType;
    }
  }

  private setObjectTypeFilter() {
    const typeSelected = this.typeSelectedSubject.value;
    if (typeSelected === this._valve) {
      const valveSet = new Set<string>(['VALVE']);
      this.updateCheckboxFilterSelection(valveSet, 'ObjectType', 'ObjectType');
    } else if (typeSelected === this._pipe) {
      const pipeSet = new Set<string>(['PIPE']);
      this.updateCheckboxFilterSelection(pipeSet, 'ObjectType', 'ObjectType');
    }
  }

  public updateCheckboxFilterSelection(
    event: Set<string>,
    filterablePropertyName: string,
    filterDisplayName: string
  ) {
    // In modal mode, prevent ObjectType changes that don't match the configured type
    if (this.isModalMode && filterablePropertyName === 'ObjectType') {
      const expectedValue = this.searchTypeFilter === 'valves' ? 'VALVE' :
                           this.searchTypeFilter === 'pipes' ? 'PIPE' : null;
      if (expectedValue && !event.has(expectedValue)) {
        // Force the correct type
        event.clear();
        event.add(expectedValue);
      }
    }

    this.latestFilterSelection = filterablePropertyName;
    this.currentPage = 0;

    if (event.size === 0) {
      this.isFilterBeingRemoved = true;
    } else {
      this.isFilterBeingRemoved = false;
    }

    if (this.isFilterBeingRemoved) {
      this.filterDataService.removeAllFilterValues(filterablePropertyName);
    } else {
      const filterableProperty = this.createFilterableProperty(
        FilterType._1,
        filterablePropertyName,
        filterDisplayName,
        Array.from(event)
      );
      this.filterDataService.setFilterValue(filterableProperty);
    }
  }

  public notTrueFalseOptionOnly(filter): boolean {
    const optionsLowerCased = filter.options.map((filterOption: string) =>
      filterOption.toLowerCase()
    );
    return !(
      optionsLowerCased.length == 2 &&
      optionsLowerCased.includes('true') &&
      optionsLowerCased.includes('false')
    );
  }

  public updateAdditionalTextFilterSelection(
    inputNumStr: string,
    filterablePropertyName: string,
    filterDisplayName: string
  ) {
    this.currentPage = 0;

    if (inputNumStr === '' || inputNumStr === null) {
      this.filterDataService.removeAllFilterValues(filterablePropertyName);
    } else {
      const selectedOperatorString = this.getSelectedOperatorStringFromSelection(
        this.selectedNominalCorrosionAllowanceComparisonOption
      );
      this.latestFilterSelection = filterablePropertyName;

      const filterOptions = [selectedOperatorString + inputNumStr];

      const filterableProperty = this.createFilterableProperty(
        FilterType._7,
        filterablePropertyName,
        filterDisplayName,
        filterOptions
      );

      this.filterDataService.setFilterValue(filterableProperty);
      this.selectedNominalCorrosionAllowanceComparisonOption = '';
    }
  }

  public updateNumericalFilterSelection(
    event: string,
    filterablePropertyName: string,
    filterDisplayName: string,
    selectedComparisonOperator: string
  ) {
    let selectedValue = event;
    if (selectedValue === '') {
      this.filterDataService.removeAllFilterValues(filterablePropertyName);
    } else {
      const selectedOperatorStr = this.getSelectedOperatorStringFromSelection(
        selectedComparisonOperator
      );

      if (this.selectedTemperatureUnit === this._celsius) {
        selectedValue = this.pipe.transform(parseFloat(selectedValue));
      }

      this.latestFilterSelection = filterablePropertyName;
      const filterableProperty = this.createFilterableProperty(
        FilterType._7,
        filterablePropertyName,
        filterDisplayName,
        [selectedOperatorStr + selectedValue]
      );

      this.filterDataService.setFilterValue(filterableProperty);
    }
  }

  public updateNominalCorrosionAllowanceComparisonOption(event) {
    this.selectedNominalCorrosionAllowanceComparisonOption = event;
  }

  public updateMaximumTemperatureComparisonOption(event) {
    this.selectedMaximumTemperatureComparisonOption = event;
  }

  public updateMinimumTemperatureComparisonOption(event) {
    this.selectedMinimumTemperatureComparisonOption = event;
  }

  /**
   * Applies both temperature filters from the combined Temperature Range accordion.
   * Handles whichever sub-group inputs are filled; empty inputs clear their filter.
   */
  public applyTemperatureFilters(): void {
    const minFilter: PipingSearchFilterModel | undefined = this.minimumTemperatureFilter;
    const maxFilter: PipingSearchFilterModel | undefined = this.maximumTemperatureFilter;
    if (minFilter) {
      this.updateNumericalFilterSelection(
        this.minTempInputText,
        minFilter.filterPropertyName ?? '',
        minFilter.filterDisplayName ?? 'Minimum Temperature',
        this.selectedMinimumTemperatureComparisonOption
      );
    }
    if (maxFilter) {
      this.updateNumericalFilterSelection(
        this.maxTempInputText,
        maxFilter.filterPropertyName ?? '',
        maxFilter.filterDisplayName ?? 'Maximum Temperature',
        this.selectedMaximumTemperatureComparisonOption
      );
    }
  }

  public popClearedFilter(filterPropertyName: string) {
    if (this.latestFilterSelection === filterPropertyName) {
      this.latestFilterSelection = '';
    }
  }

  //This is the control point for if we want to count filters with empty options
  private setFilterCounts(pipingSearchFilterModels: PipingSearchFilterModel[]) {
    this.pipeFilterCount = pipingSearchFilterModels.filter(
      (filterModel) => filterModel.filterCategory === this._pipeFilter && (!filterModel.options || filterModel.options.length > 0)
    ).length;
    this.valveFilterCount = pipingSearchFilterModels.filter(
      (filterModel) => filterModel.filterCategory === this._valveFilter && (!filterModel.options || filterModel.options.length > 0)
    ).length;
    this.commonFilterCount = pipingSearchFilterModels.filter(
      (filterModel) => filterModel.filterCategory === this._commonFilter && (!filterModel.options || filterModel.options.length === 0)
    ).length;
  }

  public updateObjectTypeSelection(clickedElementId: string) {
    // Skip this method if in modal mode since checkboxes are hidden
    if (this.isModalMode) {
      return;
    }

    const valvesChecked: boolean =
      this.valvesRadioButtonInput.nativeElement.checked;
    const pipesChecked: boolean =
      this.pipesRadioButtonInput.nativeElement.checked;

    if (valvesChecked && pipesChecked) {
      this.filterDataService.removeAllFilters();
      this.setTypeSelection(this._both);
    } else if (valvesChecked && !pipesChecked) {
      this.setTypeSelection(this._valve);
    } else if (pipesChecked && !valvesChecked) {
      this.setTypeSelection(this._pipe);
    } else if (!valvesChecked && !pipesChecked) {
      if (clickedElementId == this._pipesCheckBoxId) {
        this.valvesRadioButtonInput.nativeElement.checked = true;
        this.setTypeSelection(this._valve);
      } else if (clickedElementId == this._valvesCheckBoxId) {
        this.pipesRadioButtonInput.nativeElement.checked = true;
        this.setTypeSelection(this._pipe);
      }
    }
    this.setObjectTypeFilter();
  }

  private getSelectedOperatorStringFromSelection(userSelection: string): string {
    switch (userSelection) {
      case (this._equals):
        return 'eq ';
      case (this._greaterThan):
        return 'gt ';
      case (this._greaterThanEquals):
        return 'ge ';
      case (this._lessThan):
        return 'lt ';
      case (this._lessThanEquals):
        return 'le '
      default:
        return 'Error: user selection is not valid';
    }
  }

  // this is the control point for the wildcard behavior
  private wrapSearchTextInWildcardsConditionally(searchText: string): string {
    let returnString = '';
    const searchTerm = this.handleHyphenatedSearchTerms(searchText);
    let notArray = searchTerm.split('+-');
    let andArray = searchTerm.split('+');

    if (notArray.length > 1) {
      notArray = notArray.map((element) => element.trim());
      for (let i = 0; i < notArray.length; i++) {
        if (i !== notArray.length - 1) {
          returnString = returnString + notArray[i] + '+-';
        } else {
          returnString = returnString + notArray[i];
        }
      }
    } else if (andArray.length > 1) {
      andArray = andArray.map((element) => element.trim());
      for (let i = 0; i < andArray.length; i++) {
        if (i !== andArray.length - 1) {
          returnString = returnString + andArray[i] + '* + ';
        } else {
          returnString = returnString + andArray[i];
        }
      }
    }
    else {
      //GETTING RID OF ALL ASTERISK AND ADDING ONE AT THE BOTTOM
      returnString = searchTerm.split('*').join('') + "*";
    }
    return returnString;
  }

  handleHyphenatedSearchTerms(userInput: string): string {
    const searchOperators = ['AND', 'OR', '+', '+-'];
    const searchTerms = userInput.split(/\s+/);
    const searchText = searchTerms.map((searchTerm) => {
      if (searchOperators.some((operator) => searchTerm.toUpperCase() === operator || searchTerm.includes(operator))) {
        return searchTerm;
      }
      if (searchTerm.includes('-') && !searchTerm.startsWith('"') && !searchTerm.endsWith('"')) {
        return `"${searchTerm}"`;
      }
      return searchTerm;
    });
    return searchText.join(' ');
  }

  public updatePage(event: PageEvent) {
    this.pageSize = event.pageSize;
    this.currentPage = event.pageIndex;
    this.search();
  }

  resetPage() {
    if (!this.isModalMode) {
      // Normal mode: reset checkboxes
      if (this.pipesRadioButtonInput?.nativeElement) {
        this.pipesRadioButtonInput.nativeElement.checked = true;
      }
      if (this.valvesRadioButtonInput?.nativeElement) {
        this.valvesRadioButtonInput.nativeElement.checked = true;
      }
      this.setTypeSelection(this._both);
    } else {
      // Modal mode: maintain the configured type filter
      if (this.searchTypeFilter === 'valves') {
        this.setTypeSelection(this._valve);
      } else if (this.searchTypeFilter === 'pipes') {
        this.setTypeSelection(this._pipe);
      } else {
        this.setTypeSelection(this._both);
      }
    }
  }

  resetFilters() {
    this.resetPage();
    this.filterDataService.removeAllFilters();

    // In modal mode, maintain the type filter
    if (this.isModalMode && this.searchTypeFilter === 'valves') {
      this.setTypeSelection(this._valve);
      setTimeout(() => {
        this.setObjectTypeFilter();
      }, 100);
    }
  }

  resetModalFilters() {
    this.resetPage();

    // Always maintain valve filter in modal mode - set FIRST before removing filters
    if (this.searchTypeFilter === 'valves') {
      this.setTypeSelection(this._valve);
      this.filterDataService.removeAllFilters();
      this.setObjectTypeFilter();
      this.search();
    } else if (this.searchTypeFilter === 'pipes') {
      this.setTypeSelection(this._pipe);
      this.filterDataService.removeAllFilters();
      this.setObjectTypeFilter();
      this.search();
    } else {
      // Regular mode - can remove all filters
      this.filterDataService.removeAllFilters();
    }
  }

  exportAllToPdf() {
    alert('export all functionality not yet implemented');
  }

  removedChip(propertyName: string, option: string): void {
    // Synthetic chip: disables the admin toggle and re-searches.
    if (propertyName === 'IncludeNonCurrent') {
      this.includeNonCurrent = false;
      this.onIncludeNonCurrentChange();
      return;
    }

    // Mark as a removal so setFilters refreshes ALL filter panels (including the last-touched one).
    this.isFilterBeingRemoved = true;
    this.latestFilterSelection = '';

    // ObjectType chip: removing it resets the type toggle back to "both" and clears the filter entirely.
    // No selection = all types, so removeAllFilterValues is correct here.
    if (propertyName === 'ObjectType') {
      this.resetPage();
      this.filterDataService.removeAllFilterValues('ObjectType');
      return;
    }

    // Signal setFilters to restore ALL filter panels to their base options (including the
    // removed filter's own panel) so no panel is left in a narrowed state after chip removal.
    this.isFilterBeingRemoved = true;
    this.latestFilterSelection = '';

    const matchingFilters: FilterableProperty[] = this.filterableProperties.filter(property => property.propertyName === propertyName);
    if (matchingFilters?.length === 1) {
      const filterableProperty: FilterableProperty = matchingFilters[0];
      filterableProperty.filterOptions = (filterableProperty.filterOptions ?? []).filter(filterOption => filterOption !== option);

      if (filterableProperty.filterOptions.length === 0) {
        // Last option removed — clear the filter entirely. Leaving an empty filter in the data
        // service causes the API to treat it as "match nothing", collapsing other filter panels.
        // No selection = no restriction, so removeAllFilterValues is the correct call.
        this.filterDataService.removeAllFilterValues(propertyName);
      } else {
        this.filterDataService.removeSingleFilterValue(propertyName, option);
      }
    }
  }

  private static readonly STATUS_PROPERTY_NAMES : Set<string> = new Set<string>(['IsArchived', 'IsDraft', 'IsRetired']);

  public hideShowArchivedDraftChip(filterableProperties: FilterableProperty[]): FilterableProperty[] {
    if (!filterableProperties) { return []; }
    // Exclude the individual status properties from the user-visible chip strip;
    // they are controlled by the admin toggle, not by user filter selections.
    const nonStatusFilters: FilterableProperty[] = filterableProperties.filter(
      (filter) => !PipingAdminSearchComponent.STATUS_PROPERTY_NAMES.has(filter.propertyName ?? '')
    );

    if (this.includeNonCurrent) {
      // Reuse the stable _nonCurrentChip reference so *ngFor does not destroy/recreate
      // the chip element on every CD cycle, which would break the (removed) event.
      nonStatusFilters.push(this._nonCurrentChip);
    }

    return nonStatusFilters;
  }

  /**
   * Returns the underlying pipe or valve entity from a search result for template and utility use.
   * Uses the objectType discriminator to determine which property to return.
   */
  getEntity(result: PipingSearchResultViewModel): any {
    return result.objectType === 'PIPE' ? result.pipe : result.valve;
  }

  /**
   * Strips search-match highlight markup (e.g. `<em>` tags) that the backend embeds in matched
   * text fields, so the modal table columns (Tag, Type, Description, etc.) render plain text
   * instead of literal HTML tags. The card view (`app-valve-panel` / `app-pipeclass-panel`) uses
   * `[innerHTML]` to actually render the highlight; the modal's mat-table uses plain interpolation,
   * so the raw markup must be removed here instead.
   */
  stripHighlightMarkup(value: string | undefined): string {
    if (!value) { return value ?? ''; }
    return ProductPanelHelper.RemoveHTML(value);
  }

  /** Called when the include-non-current toggle changes. Resets to page 1 and re-runs the search. */
  onIncludeNonCurrentChange(): void {
    this.currentPage = 0;
    this.search();
  }

  setValveSelected(valveSelected: boolean): void {
    this.valveSelected = valveSelected;
  }

  setPipeClassSelected(pipeClassSelected: boolean): void {
    this.pipeClassSelected = pipeClassSelected;
  }

  setValveId(valveId: string): void {
    this.valveId = valveId;
  }

  setPipeClassId(pipeClassId: string): void {
    this.pipeClassId = pipeClassId;
  }

  convertAbbreviationIntoSymbol(chipString: string): string {
    if (chipString.includes('eq')) {
      chipString = chipString.replace('eq', '=');
    } else if (chipString.includes('gt')) {
      chipString = chipString.replace('gt', '>');
    } else if (chipString.includes('ge')) {
      chipString = chipString.replace('ge', '≥');
    } else if (chipString.includes('lt')) {
      chipString = chipString.replace('lt', '<');
    } else if (chipString.includes('le')) {
      chipString = chipString.replace('le', '≤');
    }

    return chipString;
  }

  /**
   * Open modal to create a new valve spec
   */
  openCreateNewSpec(): void {
    const dialogData: ValveSpecModalData = {
      mode: 'create'
    };

    this.dialog.open(ValveSpecModalComponent, {
      width: '650px',
      disableClose: false,
      data: dialogData
    });
  }
}
